import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update description
    updateMetaTag('description', description);

    // Update keywords if provided
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${canonical}`);
    }

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    if (canonical) {
      updateMetaTag('og:url', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${canonical}`, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (canonical) {
      updateMetaTag('twitter:url', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${canonical}`);
    }

    // Update last-modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, schema]);
};

// Helper function to generate organization schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Thyamis',
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  logo: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/vite.svg`,
  description: 'Professional IT Solutions, Hardware, Software & AI Services',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Energieweg 2',
    addressLocality: 'Mijdrecht',
    postalCode: '3641RT',
    addressCountry: 'NL',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 52.205555,
    longitude: 4.859428,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+31-616300011',
    contactType: 'sales',
    email: 'sales@thyamis.com',
    availableLanguage: ['en', 'nl'],
  },
  sameAs: [
    'https://www.linkedin.com/company/thyamis/about/',
  ],
});

// Helper function to generate local business schema
export const getLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
  name: 'Thyamis',
  image: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/vite.svg`,
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  telephone: '+31-616300011',
  email: 'sales@thyamis.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Energieweg 2',
    addressLocality: 'Mijdrecht',
    addressRegion: 'Utrecht',
    postalCode: '3641RT',
    addressCountry: 'NL',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 52.205555,
    longitude: 4.859428,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
  ],
  priceRange: '$$',
});

// Helper function to generate breadcrumb schema
export const getBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${item.url}`,
  })),
});

// Helper function to generate service schema
export const getServiceSchema = (name: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: 'Thyamis',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Netherlands',
  },
  serviceType: 'IT Solutions',
});

// Helper function to generate product schema
export const getProductSchema = (name: string, description: string, category: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  category,
  brand: {
    '@type': 'Brand',
    name: 'Thyamis',
  },
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceCurrency: 'EUR',
  },
});

// Helper function to generate FAQ schema
export const getFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Helper function to generate contact page schema
export const getContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Thyamis',
  description: 'Get in touch with Thyamis for IT solutions, technical support, or partnership opportunities.',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
  mainEntity: {
    '@type': 'Organization',
    name: 'Thyamis',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+31-616300011',
        contactType: 'sales',
        email: 'sales@thyamis.com',
        availableLanguage: ['en', 'nl'],
        areaServed: 'NL',
      },
      {
        '@type': 'ContactPoint',
        email: 'info@thyamis.com',
        contactType: 'customer service',
        availableLanguage: ['en', 'nl'],
        areaServed: 'NL',
      },
    ],
  },
});

// Helper function to generate about page schema
export const getAboutPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Thyamis',
  description: 'Learn about Thyamis, a leading provider of comprehensive IT solutions since 2025.',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
  mainEntity: {
    '@type': 'Organization',
    name: 'Thyamis',
    foundingDate: '2025',
    description: 'Leading provider of comprehensive IT solutions including hardware, software, cloud services, and AI implementation.',
  },
});
